import { useRef, useState } from 'react'
import axios from 'axios'
import { useQuery } from '@tanstack/react-query'
import { API_BASE_URL } from '../constants/system'
import { getAugementedTOC } from '../utils/multitables'
import useAlerts, { useAlertRecipients, type AlertTableInput } from './useAlerts'
import {
  addDraftTable,
  createAlertBody,
  draftFromRule,
  draftProblems,
  emptyAlertDraft,
  sameDraftTable,
  updateAlertBody,
  type AlertSetupDraft,
  type AlertSetupKind,
} from '../utils/alertSetupDraft'
import { buildAlertFirstMessage } from '../utils/alertSetupMessage'
import { defaultAlertSchedule } from '../utils/alertSchedule'
import { alertSourceOf } from '../utils/alertSources'
import { refuse, type WhoToAsk } from '../utils/permissionReason'
import type { GenNode } from '../types/carbon/models/GenNode'
import type { AlertRule } from '../types/gotypes'

// Module level, so react-query keeps the selected nodes stable between
// renders: the card's tree opens its folders once per loaded TOC.
const selectTocNodes = (toc: GenNode[]) => getAugementedTOC({ toc, multiTables: [] }).nodes

const NO_SCHEDULE = { every: 'daily' as const, at: '', timezone: '' }

/** Where an unsent New alert card waits, so leaving the page and coming back keeps it. */
export const alertDraftStorageKey = (customer: string, email: string) => `alert-setup-draft:${customer}:${email.toLowerCase()}:new`

function readStoredDraft(customer: string, email: string): AlertSetupDraft | null {
  try {
    const raw = sessionStorage.getItem(alertDraftStorageKey(customer, email))
    return raw ? (JSON.parse(raw) as AlertSetupDraft) : null
  } catch {
    return null
  }
}

function storeDraft(customer: string, email: string, draft: AlertSetupDraft | null) {
  try {
    if (draft) sessionStorage.setItem(alertDraftStorageKey(customer, email), JSON.stringify(draft))
    else sessionStorage.removeItem(alertDraftStorageKey(customer, email))
  } catch {
    // Storage blocked: the card still works, it just will not survive leaving the page.
  }
}

export interface UseAlertSetupDraftArgs {
  customer: string
  /** `?alert=`: 'new' for a new alert, else the id of the alert to change. Empty: not in alert mode. */
  alertParam: string
  /** The job a new card starts on. */
  defaultJob: string
  viewerEmail: string
  whoToAsk: WhoToAsk
  jobLabel?: (job: string) => string
}

/**
 * The alert card on the agent start screen: its contents, the data it needs
 * (the job's TOC, the recipient candidates, the rule for `?alert=<id>`) and
 * the two writes Send makes around the session create (save the rule, then
 * tie the session to it). The contents live here, on the page, so a failed
 * Send keeps every field; an unsent New alert also waits in sessionStorage,
 * and once saved the rule itself is the copy (the page swaps `?alert=new`
 * for its id, so Back from the chat reopens it filled in).
 */
export function useAlertSetupDraft({ customer, alertParam, defaultJob, viewerEmail, whoToAsk, jobLabel }: UseAlertSetupDraftArgs) {
  const isNew = alertParam === 'new'
  const alertId = alertParam && !isNew ? alertParam : ''
  const enabled = Boolean(alertParam)

  const alerts = useAlerts({ customer, alertId: alertId || undefined, list: false, evaluations: false })
  const loadedRule = alerts.alert ?? null

  const [draft, setDraft] = useState<AlertSetupDraft>(() =>
    (isNew && readStoredDraft(customer, viewerEmail)) || emptyAlertDraft(defaultJob, defaultAlertSchedule()),
  )
  // The rule this card last saved (created or updated): the baseline for
  // the next save, so a second Send after a refusal never creates twice.
  const [savedRule, setSavedRule] = useState<AlertRule | null>(null)
  const [createdHere, setCreatedHere] = useState(false)
  const [showProblems, setShowProblems] = useState(false)
  const [recipientQuery, setRecipientQuery] = useState('')

  // Fill the card from the saved rule once per alert id (render-phase ref
  // guard). A rule this card has just created is already what it shows.
  const prefilledRef = useRef('')
  if (alertId && loadedRule && loadedRule.id === alertId && prefilledRef.current !== alertId) {
    prefilledRef.current = alertId
    setDraft(draftFromRule(loadedRule, defaultAlertSchedule()))
  }

  const tocQuery = useQuery({
    queryKey: ['toc', customer, draft.job],
    enabled: Boolean(enabled && customer && draft.job && draft.kind === 'table'),
    queryFn: async () => {
      const res = await axios.get<{ toc: GenNode[] }>(`${API_BASE_URL}/job/${customer}/${draft.job}/toc`)
      return res.data.toc || []
    },
    select: selectTocNodes,
  })

  const recipients = useAlertRecipients({ customer, q: recipientQuery, enabled })

  const baselineRule = savedRule ?? loadedRule
  const owner = baselineRule?.user ?? ''
  const isOwner = !owner || owner.toLowerCase() === viewerEmail.toLowerCase()
  // Only the owner ties a chat to the rule (setup-session is owner only),
  // and an event alert has no check for an agent to write.
  const lockedReason = !baselineRule
    ? undefined
    : !isOwner
      ? refuse("You can't change a colleague's alert with the agent.", whoToAsk, { owner }).reason
      : alertSourceOf(baselineRule).kind === 'event'
        ? 'An event alert has no check to write. Edit it on the Alerts page.'
        : undefined
  const loading = Boolean(alertId) && alerts.alertIsLoading && !savedRule
  const loadError = alertId && alerts.alertIsError && !savedRule
    ? (alerts.alertError instanceof Error ? alerts.alertError.message : 'Could not load the alert.')
    : null

  const problems = draftProblems(draft)
  const valid = Object.keys(problems).length === 0

  const update = (next: AlertSetupDraft) => {
    setDraft(next)
    if (isNew && !savedRule) storeDraft(customer, viewerEmail, next)
  }

  const handleChange = (patch: Partial<Pick<AlertSetupDraft, 'name' | 'question' | 'condition' | 'context' | 'schedule' | 'recipients'>>) =>
    update({ ...draft, ...patch })

  const handleKindChange = (kind: AlertSetupKind) => update({ ...draft, kind })

  // Every table of an alert is on one job: a new job starts the list again.
  const handleJobChange = (job: string) => {
    if (job === draft.job) return
    update({ ...draft, job, tables: [] })
  }

  const handleAddTable = (t: AlertTableInput) => update({ ...draft, tables: addDraftTable(draft.tables, { ...t, job: draft.job }) })

  const handleRemoveTable = (t: AlertTableInput) => update({ ...draft, tables: draft.tables.filter((x) => !sameDraftTable(x, t)) })

  /** Send's first check: false (and every problem shown) when the card cannot be saved yet. */
  const handleValidate = (): boolean => {
    setShowProblems(true)
    return valid && !lockedReason && !loading && !loadError
  }

  /**
   * Save the card: POST for a new alert, else a PUT of what changed (none:
   * no request). Resolves to the rule; rejects with the server's error.
   */
  const handleSaveRule = async (): Promise<AlertRule> => {
    if (!baselineRule) {
      const rule = await alerts.createAlert(createAlertBody(draft))
      prefilledRef.current = rule.id
      setSavedRule(rule)
      setCreatedHere(true)
      storeDraft(customer, viewerEmail, null)
      return rule
    }
    // A rule with no schedule compares against one that matches nothing, so
    // the card's schedule is always sent and the rule comes due.
    const body = updateAlertBody(draft, draftFromRule(baselineRule, NO_SCHEDULE))
    if (Object.keys(body).length === 0) return baselineRule
    const rule = await alerts.updateAlert(baselineRule.id, body, { quiet: true })
    setSavedRule(rule)
    return rule
  }

  const handleAttachSession = (ruleId: string, sessionId: string) => alerts.attachSetupSession(ruleId, sessionId)

  /** The chat's first message: the card in prose, then the user's words. */
  const firstMessage = (words: string) =>
    buildAlertFirstMessage(draft, words, { mode: isNew || createdHere ? 'create' : 'rewrite', jobLabel })

  return {
    enabled,
    /** Nothing saved yet: once the rule exists its kind is fixed and the card says "Change alert". */
    isNew: isNew && !savedRule,
    draft,
    problems: showProblems ? problems : {},
    valid,
    loading,
    loadError,
    lockedReason,
    toc: tocQuery.data ?? [],
    tocLoading: tocQuery.isLoading,
    recipientOptions: recipients.options,
    recipientsLoading: recipients.loading,
    recipientsError: recipients.error,
    recipientQuery,
    handleRecipientQueryChange: setRecipientQuery,
    handleChange,
    handleKindChange,
    handleJobChange,
    handleAddTable,
    handleRemoveTable,
    handleValidate,
    handleSaveRule,
    handleAttachSession,
    firstMessage,
  }
}

export default useAlertSetupDraft
